import { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, Search, X } from 'lucide-react';
import { checkLogin, setOtpVerified } from '../../Common/store';
import { isValidEmail, isValidPhone, normalizePhone } from '../../Common/hooks/useManualPaymentConfig';
import { getMappedTerminalPharmacies, submitMappedTerminalPayment } from '../api/cp';

function normalizePharmacy(row) {
  if (!row) return null;
  const mappingId = row.mappingId ?? row.mapping_id;
  if (mappingId === undefined || mappingId === null || mappingId === '') return null;
  return {
    mappingId: Number(mappingId),
    pharmacyId: row.pharmacyId ?? row.pharmacy_id ?? row.id,
    institutionCode: String(row.institutionCode ?? row.institution_code ?? ''),
    externalCustomerCode: String(row.externalCustomerCode ?? row.external_customer_code ?? row.code ?? ''),
    name: String(row.name ?? row.customer_name ?? ''),
    phone: String(row.phone ?? row.pharmacy_phone ?? ''),
    address: row.address ?? '',
    businessNo: String(row.businessNo ?? row.business_no ?? ''),
    representativeName: String(row.representativeName ?? row.representative_name ?? ''),
  };
}

function byteLength(value) {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(String(value || '')).length;
  return unescape(encodeURIComponent(String(value || ''))).length;
}

function PharmacyPicker({ open, onClose, onSelect, refreshKey = 0 }) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getMappedTerminalPharmacies({ q: query.trim(), page, limit: 20 });
        const nextRows = Array.isArray(data?.rows) ? data.rows.map(normalizePharmacy).filter(Boolean) : [];
        setRows(nextRows);
        setTotal(Number(data?.total) || nextRows.length);
      } catch (e) {
        setRows([]);
        setTotal(0);
        setError(e?.message || '담당 거래처를 불러오지 못했습니다.');
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => clearTimeout(timer);
  }, [open, query, page, refreshKey]);

  useEffect(() => {
    if (open) setPage(1);
  }, [open, query]);

  if (!open) return null;
  const lastPage = Math.max(1, Math.ceil(total / 20));

  return (
    <div role="presentation" onClick={onClose} style={styles.overlay}>
      <div role="dialog" aria-modal="true" aria-label="담당 거래처 선택" onClick={(e) => e.stopPropagation()} style={styles.sheet}>
        <div style={styles.sheetHeader}>
          <strong>담당 거래처 선택</strong>
          <button type="button" onClick={onClose} style={styles.iconButton} aria-label="닫기"><X size={21} /></button>
        </div>
        <div style={styles.searchWrap}>
          <Search size={18} color="#8b95a1" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="거래처코드, 약국명, 사업자번호 검색"
            style={styles.searchInput}
          />
        </div>
        <div style={styles.resultArea}>
          {loading && <p style={styles.empty}>담당 거래처를 불러오는 중입니다.</p>}
          {!loading && error && <p style={{ ...styles.empty, color: '#e5484d' }}>{error}</p>}
          {!loading && !error && rows.length === 0 && <p style={styles.empty}>담당자에게 매칭된 약국이 없습니다.</p>}
          {!loading && rows.map((row) => (
            <button
              type="button"
              key={row.mappingId}
              onClick={() => { onSelect(row); onClose(); }}
              style={styles.resultButton}
            >
              <span style={styles.resultTitle}>{row.externalCustomerCode || row.institutionCode || '-'} · {row.name}</span>
              <span style={styles.resultMeta}>사업자번호 {row.businessNo || '-'}{row.phone ? ' · ' + row.phone : ''}</span>
            </button>
          ))}
        </div>
        {total > 20 && (
          <div style={styles.pagination}>
            <button type="button" disabled={page <= 1} onClick={() => setPage((v) => Math.max(1, v - 1))} style={styles.pageButton}>이전</button>
            <span>{page} / {lastPage}</span>
            <button type="button" disabled={page >= lastPage} onClick={() => setPage((v) => Math.min(lastPage, v + 1))} style={styles.pageButton}>다음</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MappedManualPayment({ config, initialPharmacy, variant = 'A' }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [selected, setSelected] = useState(() => normalizePharmacy(initialPharmacy));
  const [pickerOpen, setPickerOpen] = useState(() => !normalizePharmacy(initialPharmacy));
  const [pickerRefreshKey, setPickerRefreshKey] = useState(0);
  const [amount, setAmount] = useState('');
  const [cardNo, setCardNo] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [installment, setInstallment] = useState('00');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'error' });
  const salespersonName = useMemo(() => {
    try {
      return window.localStorage.getItem('app_saved_terminal_name') || '';
    } catch (_) {
      return '';
    }
  }, []);

  const productName = selected
    ? String(selected.externalCustomerCode || '') + '_' + String(selected.name || '')
    : '';
  const productBytes = useMemo(() => byteLength(productName), [productName]);
  const productTooLong = productBytes > Number(config?.productNameMaxBytes || 255);
  const normalizedPhone = normalizePhone(phone);
  const amountNumber = Number(amount || 0);

  const valid = Boolean(
    selected?.mappingId &&
    amountNumber > 0 &&
    cardNo.length === 16 &&
    Number(expMonth) >= 1 && Number(expMonth) <= 12 &&
    expYear.length === 2 &&
    (!config?.buyerEmailRequired || isValidEmail(email)) &&
    (!config?.buyerPhoneRequired || isValidPhone(normalizedPhone)) &&
    !productTooLong
  );

  const showToast = (message, type = 'error') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast((prev) => ({ ...prev, visible: false })), 2000);
  };

  const submit = async () => {
    if (!valid || submitting) return;
    setSubmitting(true);
    try {
      const result = await submitMappedTerminalPayment({
        customerMapId: selected.mappingId,
        mchtTrdNo: 'manual_' + Date.now() + '_' + Math.random().toString(36).slice(2, 9),
        trdAmt: amountNumber,
        instmtMon: installment,
        mchtCustNm: selected.name,
        mchtCustId: selected.externalCustomerCode,
        email: email.trim(),
        phone: normalizedPhone,
        cardNo,
        vldDtMon: expMonth.padStart(2, '0'),
        vldDtYear: expYear.slice(-2),
      });
      const status = String(result?.status || '').toUpperCase();
      if (status && status !== 'COMPLETED') {
        throw new Error(result?.outRsltMsg || result?.provider_result?.outRsltMsg || '결제에 실패했습니다.');
      }
      showToast('결제가 완료되었습니다.', 'success');
      setAmount('');
      setCardNo('');
      setExpMonth('');
      setExpYear('');
      setInstallment('00');
      setEmail('');
      setPhone('');
    } catch (e) {
      const code = Number(e?.code ?? e?.response?.data?.code);
      if (code === 4040) {
        setSelected(null);
        setPickerRefreshKey((v) => v + 1);
        setPickerOpen(true);
        showToast('선택한 거래처 매핑이 변경되었습니다. 다시 선택해 주세요.');
      } else if (code === 9999) {
        dispatch(setOtpVerified(false));
        dispatch(checkLogin(false));
        navigate('/app/login', { replace: true });
      } else {
        showToast(e?.message || e?.response?.data?.msg || '결제 요청 중 오류가 발생했습니다.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (variant === 'B') {
    return (
      <div style={bStyles.page}>
        {toast.visible && (
          <div role="status" style={{ ...styles.toast, background: toast.type === 'success' ? '#0b7a3e' : '#e5484d' }}>
            {toast.message}
          </div>
        )}
        <div style={bStyles.scroll}>
          <div style={bStyles.form}>
            <div style={bStyles.row}>
              <div style={{ ...bStyles.field, flex: 2 }}>
                <label style={bStyles.label}>거래처명</label>
                <input
                  type="text"
                  readOnly
                  value={selected?.name || ''}
                  placeholder="거래처명"
                  aria-label="담당 거래처 선택"
                  onClick={() => setPickerOpen(true)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') setPickerOpen(true);
                  }}
                  style={{ ...bStyles.input, cursor: 'pointer', color: selected ? '#191f28' : '#9aa4b5' }}
                />
              </div>
              <div style={{ ...bStyles.field, flex: 1 }}>
                <label style={bStyles.label}>영업자명</label>
                <input
                  type="text"
                  readOnly
                  value={salespersonName}
                  placeholder="영업자명"
                  aria-label="영업자명"
                  title="자동 매핑되어 수정할 수 없습니다."
                  style={{ ...bStyles.input, ...bStyles.fixedInput }}
                />
              </div>
            </div>

            <div style={bStyles.row}>
              <div style={{ ...bStyles.field, flex: 1 }}>
                <label style={bStyles.label}>상품명</label>
                <input
                  type="text"
                  readOnly
                  value={productName}
                  placeholder="상품명"
                  aria-label="상품명"
                  title="거래처 정보로 자동 생성되어 수정할 수 없습니다."
                  style={{ ...bStyles.input, ...bStyles.fixedInput }}
                />
                {productTooLong && <small style={bStyles.error}>상품명이 허용 길이를 초과했습니다.</small>}
              </div>
            </div>

            <div style={bStyles.row}>
              <div style={{ ...bStyles.field, flex: 2 }}>
                <label style={bStyles.label}>카드번호</label>
                <input
                  name="mappedCardNo"
                  value={cardNo.replace(/(\d{4})(?=\d)/g, '$1 ')}
                  onChange={(event) => {
                    const value = event.target.value.replace(/\D/g, '').slice(0, 16);
                    setCardNo(value);
                    if (value.length === 16) {
                      window.setTimeout(() => document.getElementById('mapped-exp-month')?.focus(), 0);
                    }
                  }}
                  inputMode="numeric"
                  autoComplete="cc-number"
                  placeholder="1234 5678 9012 3456"
                  style={bStyles.input}
                />
              </div>
              <div style={{ ...bStyles.field, flex: 1 }}>
                <label style={bStyles.label}>유효기간</label>
                <div style={bStyles.expiration}>
                  <input
                    id="mapped-exp-month"
                    value={expMonth}
                    onChange={(event) => {
                      const value = event.target.value.replace(/\D/g, '').slice(0, 2);
                      setExpMonth(value);
                      if (value.length === 2) window.setTimeout(() => document.getElementById('mapped-exp-year')?.focus(), 0);
                    }}
                    inputMode="numeric"
                    autoComplete="cc-exp-month"
                    placeholder="월"
                    style={bStyles.expirationInput}
                  />
                  <span style={bStyles.expirationSlash}>/</span>
                  <input
                    id="mapped-exp-year"
                    value={expYear}
                    onChange={(event) => {
                      const value = event.target.value.replace(/\D/g, '').slice(0, 2);
                      setExpYear(value);
                      if (value.length === 2) window.setTimeout(() => document.getElementById('mapped-amount')?.focus(), 0);
                    }}
                    inputMode="numeric"
                    autoComplete="cc-exp-year"
                    placeholder="년"
                    style={bStyles.expirationInput}
                  />
                </div>
              </div>
            </div>

            <div style={bStyles.row}>
              <div style={{ ...bStyles.field, flex: 2 }}>
                <label style={bStyles.label}>결제금액</label>
                <input
                  id="mapped-amount"
                  value={amount ? Number(amount).toLocaleString('ko-KR') : ''}
                  onChange={(event) => setAmount(event.target.value.replace(/\D/g, '').slice(0, 9))}
                  inputMode="numeric"
                  placeholder="금액"
                  style={bStyles.input}
                />
              </div>
              <div style={{ ...bStyles.field, flex: 1 }}>
                <label style={bStyles.label}>할부</label>
                <div style={bStyles.selectWrap}>
                  <select value={installment} onChange={(event) => setInstallment(event.target.value)} style={bStyles.select}>
                    <option value="00">일시불</option>
                    {Array.from({ length: 10 }, (_, index) => String(index + 1).padStart(2, '0')).map((value) => (
                      <option key={value} value={value}>{value}개월</option>
                    ))}
                  </select>
                  <ChevronDown size={20} color="#6b7280" style={bStyles.selectIcon} />
                </div>
              </div>
            </div>

            {config?.buyerEmailRequired && (
              <div style={bStyles.row}>
                <div style={{ ...bStyles.field, flex: 1 }}>
                  <label style={bStyles.label}>구매자 이메일</label>
                  <input
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    type="email"
                    autoComplete="email"
                    placeholder="필수 입력"
                    style={bStyles.input}
                  />
                </div>
              </div>
            )}

            {config?.buyerPhoneRequired && (
              <div style={bStyles.row}>
                <div style={{ ...bStyles.field, flex: 1 }}>
                  <label style={bStyles.label}>구매자 연락처</label>
                  <input
                    value={phone}
                    onChange={(event) => setPhone(event.target.value.replace(/\D/g, '').slice(0, 11))}
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="필수 입력"
                    style={bStyles.input}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        <div style={bStyles.bottom}>
          <button
            type="button"
            onClick={submit}
            disabled={!valid || submitting}
            style={{ ...bStyles.submit, backgroundColor: valid && !submitting ? 'var(--brand-primary)' : '#cbd5e1' }}
          >
            {submitting ? '요청중...' : '결제하기'}
          </button>
        </div>
        <PharmacyPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={setSelected} refreshKey={pickerRefreshKey} />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {toast.visible && (
        <div role="status" style={{ ...styles.toast, background: toast.type === 'success' ? '#0b7a3e' : '#e5484d' }}>
          {toast.message}
        </div>
      )}
      <div style={styles.scroll}>
        <section style={styles.card}>
          <div style={styles.sectionHeader}>
            <strong>담당 거래처</strong>
            <button type="button" onClick={() => setPickerOpen(true)} style={styles.linkButton}>{selected ? '변경' : '선택'}</button>
          </div>
          {!selected ? (
            <button type="button" onClick={() => setPickerOpen(true)} style={styles.selectPrompt}>담당 거래처를 선택해 주세요.</button>
          ) : (
            <div style={styles.readonlyGrid}>
              <Info label="거래처코드" value={selected.externalCustomerCode || selected.institutionCode || '-'} />
              <Info label="약국명" value={selected.name || '-'} />
              <Info label="사업자번호" value={selected.businessNo || '-'} />
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <label style={styles.label}>상품명</label>
            <div style={{ ...styles.readonlyValue, ...styles.fixedReadonlyValue }}>{productName || '거래처 선택 후 자동 생성됩니다.'}</div>
            {selected && (
              <small style={{ color: productTooLong ? '#e5484d' : '#8b95a1' }}>
                UTF-8 {productBytes}/{config?.productNameMaxBytes || 255} bytes · 서버에서 최종 생성
              </small>
            )}
          </div>
        </section>

        <section style={styles.card}>
          <strong style={{ display: 'block', marginBottom: 14 }}>결제정보</strong>
          <Field label="결제금액">
            <input value={amount ? Number(amount).toLocaleString('ko-KR') : ''} onChange={(e) => setAmount(e.target.value.replace(/\D/g, '').slice(0, 9))} inputMode="numeric" placeholder="금액 입력" style={styles.input} />
          </Field>
          <Field label="카드번호">
            <input value={cardNo.replace(/(\d{4})(?=\d)/g, '$1 ')} onChange={(e) => setCardNo(e.target.value.replace(/\D/g, '').slice(0, 16))} inputMode="numeric" placeholder="카드번호 16자리" autoComplete="cc-number" style={styles.input} />
          </Field>
          <div style={{ display: 'flex', gap: 10 }}>
            <Field label="유효기간 월" grow>
              <input value={expMonth} onChange={(e) => setExpMonth(e.target.value.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" placeholder="MM" autoComplete="cc-exp-month" style={styles.input} />
            </Field>
            <Field label="유효기간 년" grow>
              <input value={expYear} onChange={(e) => setExpYear(e.target.value.replace(/\D/g, '').slice(0, 2))} inputMode="numeric" placeholder="YY" autoComplete="cc-exp-year" style={styles.input} />
            </Field>
            <Field label="할부" grow>
              <select value={installment} onChange={(e) => setInstallment(e.target.value)} style={styles.input}>
                <option value="00">일시불</option>
                {Array.from({ length: 10 }, (_, i) => String(i + 1).padStart(2, '0')).map((v) => <option key={v} value={v}>{v}개월</option>)}
              </select>
            </Field>
          </div>
          {config?.buyerEmailRequired && (
            <Field label="구매자 이메일">
              <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" autoComplete="email" placeholder="buyer@example.com" style={styles.input} />
            </Field>
          )}
          {config?.buyerPhoneRequired && (
            <Field label="구매자 연락처">
              <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 11))} type="tel" inputMode="tel" autoComplete="tel" placeholder="01012345678" style={styles.input} />
            </Field>
          )}
        </section>
      </div>
      <div style={styles.bottom}>
        <button type="button" onClick={submit} disabled={!valid || submitting} style={{ ...styles.submit, opacity: valid && !submitting ? 1 : 0.45 }}>
          {submitting ? '결제 요청 중...' : '결제하기'}
        </button>
      </div>
      <PharmacyPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={setSelected} refreshKey={pickerRefreshKey} />
    </div>
  );
}

function Info({ label, value }) {
  return <div><span style={styles.infoLabel}>{label}</span><strong style={styles.infoValue}>{value}</strong></div>;
}

function Field({ label, children, grow = false }) {
  return <label style={{ ...styles.field, ...(grow ? { flex: 1 } : {}) }}><span style={styles.label}>{label}</span>{children}</label>;
}

const bStyles = {
  page: {
    position: 'relative',
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
    color: '#191f28',
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    paddingBottom: 96,
    background: '#fff',
  },
  form: { padding: '20px 26px 24px', boxSizing: 'border-box' },
  row: { display: 'flex', alignItems: 'flex-start', marginBottom: 13, gap: 12 },
  field: { minWidth: 0 },
  label: {
    display: 'block',
    padding: '0 4px',
    marginBottom: 2,
    color: '#666',
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.45,
  },
  input: {
    width: '100%',
    height: 35,
    minWidth: 0,
    boxSizing: 'border-box',
    padding: '2px 4px',
    border: 0,
    borderBottom: '1px solid #f0f0f0',
    borderRadius: 0,
    background: 'transparent',
    color: '#191f28',
    outline: 'none',
    fontSize: 20,
    fontWeight: 600,
    lineHeight: '30px',
    fontVariantNumeric: 'tabular-nums',
  },
  fixedInput: {
    paddingLeft: 8,
    paddingRight: 8,
    borderBottomColor: '#d9dee5',
    borderRadius: '6px 6px 0 0',
    background: '#f2f4f6',
    color: '#6b7684',
    cursor: 'default',
  },
  expiration: {
    display: 'flex',
    height: 35,
    alignItems: 'center',
    gap: 4,
    borderBottom: '1px solid #f0f0f0',
  },
  expirationInput: {
    width: 35,
    minWidth: 0,
    padding: '2px 4px',
    border: 0,
    background: 'transparent',
    color: '#191f28',
    outline: 'none',
    fontSize: 20,
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums',
  },
  expirationSlash: { flexShrink: 0, color: '#6b7280', fontSize: 18, fontWeight: 500 },
  selectWrap: { position: 'relative', height: 35 },
  select: {
    width: '100%',
    height: 35,
    padding: '0 28px 0 4px',
    border: 0,
    borderBottom: '1px solid #f0f0f0',
    borderRadius: 0,
    background: 'transparent',
    color: '#191f28',
    outline: 'none',
    appearance: 'none',
    WebkitAppearance: 'none',
    fontSize: 20,
    fontWeight: 600,
  },
  selectIcon: {
    position: 'absolute',
    right: 4,
    top: '50%',
    transform: 'translateY(-50%)',
    pointerEvents: 'none',
  },
  error: { display: 'block', marginTop: 4, padding: '0 4px', color: '#e5484d', fontSize: 11 },
  bottom: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 50,
    padding: '12px 16px calc(10px + env(safe-area-inset-bottom, 0px))',
    boxSizing: 'border-box',
    background: '#fff',
  },
  submit: {
    width: '100%',
    padding: 16,
    border: 0,
    borderRadius: 16,
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
  },
};

const styles = {
  page: { position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#f6f8f7' },
  scroll: { flex: 1, overflowY: 'auto', padding: '16px 16px 110px' },
  card: { background: '#fff', borderRadius: 16, padding: 16, marginBottom: 14, boxShadow: '0 3px 14px rgba(0,0,0,0.06)' },
  sectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  linkButton: { border: 0, background: '#eff8f2', color: 'var(--brand-primary)', borderRadius: 8, padding: '7px 12px', fontWeight: 700, cursor: 'pointer' },
  selectPrompt: { width: '100%', padding: 16, border: '1px dashed #b8dec7', borderRadius: 12, background: '#f8fcf9', color: '#076931', fontWeight: 700 },
  readonlyGrid: { display: 'grid', gridTemplateColumns: '1fr', gap: 10, padding: 12, background: '#f8faf9', borderRadius: 12 },
  readonlyValue: { minHeight: 38, padding: '10px 0', borderBottom: '1px solid #e5e8eb', color: '#191f28', fontWeight: 650, wordBreak: 'break-all' },
  fixedReadonlyValue: { padding: '10px 12px', borderBottomColor: '#d9dee5', borderRadius: 8, background: '#f2f4f6', color: '#6b7684' },
  infoLabel: { display: 'block', fontSize: 12, color: '#8b95a1', marginBottom: 2 },
  infoValue: { display: 'block', fontSize: 15, color: '#191f28' },
  field: { display: 'flex', flexDirection: 'column', marginBottom: 13, minWidth: 0 },
  label: { fontSize: 13, fontWeight: 650, color: '#4e5968', marginBottom: 6 },
  input: { width: '100%', minWidth: 0, boxSizing: 'border-box', border: '1px solid #e5e8eb', borderRadius: 10, padding: '12px', background: '#fff', fontSize: 16, outline: 'none' },
  bottom: { position: 'absolute', left: 0, right: 0, bottom: 0, padding: '12px 16px calc(12px + env(safe-area-inset-bottom, 0px))', background: 'rgba(255,255,255,0.96)', borderTop: '1px solid #edf0ee' },
  submit: { width: '100%', border: 0, borderRadius: 14, padding: 15, background: 'var(--brand-primary)', color: '#fff', fontSize: 16, fontWeight: 750 },
  toast: { position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)', zIndex: 3000, color: '#fff', borderRadius: 10, padding: '10px 16px', boxShadow: '0 6px 20px rgba(0,0,0,0.18)', maxWidth: 'calc(100% - 32px)' },
  overlay: { position: 'fixed', inset: 0, zIndex: 2500, background: 'rgba(0,0,0,0.45)', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' },
  sheet: { width: '100%', maxWidth: 768, maxHeight: '82dvh', background: '#fff', borderRadius: '18px 18px 0 0', padding: 16, boxSizing: 'border-box', display: 'flex', flexDirection: 'column' },
  sheetHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 17, marginBottom: 12 },
  iconButton: { border: 0, background: 'transparent', padding: 6, display: 'flex' },
  searchWrap: { display: 'flex', alignItems: 'center', gap: 8, border: '1px solid #e5e8eb', borderRadius: 10, padding: '0 12px', marginBottom: 12 },
  searchInput: { flex: 1, border: 0, outline: 0, padding: '12px 0', fontSize: 14, minWidth: 0 },
  resultArea: { overflowY: 'auto', minHeight: 180 },
  resultButton: { width: '100%', display: 'flex', flexDirection: 'column', textAlign: 'left', padding: '12px 4px', border: 0, borderBottom: '1px solid #f0f2f1', background: '#fff' },
  resultTitle: { fontSize: 15, fontWeight: 700, color: '#191f28' },
  resultMeta: { marginTop: 4, fontSize: 12, color: '#8b95a1' },
  empty: { textAlign: 'center', color: '#8b95a1', padding: '44px 8px' },
  pagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, paddingTop: 12 },
  pageButton: { border: '1px solid #e5e8eb', borderRadius: 8, background: '#fff', padding: '7px 12px' },
};
